import { useMemo } from "react";
import { useCompanyFilterContext } from "../../components/context/CompanyFilterContext";
import { useActiveInactiveCompanies } from "./useActiveInactiveCompanies";
import { useCompaniesCredit } from "./useCompaniesCredit";
import { useCompaniesAiCost } from "./useCompaniesAiCost";
import { useBadRecordsFailed } from "./useBadRecordsFailed";
import { useFailedCasesByCompany } from "./useFailedCasesByCompany";
import { useCompanySubscriptionsCredit } from "./useCompanySubscriptionsCredit";
import { useCompanyStatsUI } from "./useCompanyStatsUI";
import { useDownloadOptions } from "../../../../hooks/useDownloadOptions";
import useUpcomingCompany from "../../../../hooks/useUpComingCompany";
import { renderSuperDashboardStatCards } from "../config/superDashboardConfig";

const useSuperDashboard = () => {
    const { filterParams } = useCompanyFilterContext();
    const { selectedRange, selectedTiers, selectedStatus, selectedCompanies } = filterParams;

    const { downloadDashboardCsvFile: _downloadDashboardCsvFile } = useDownloadOptions();
    const downloadDashboardCsvFile = () => _downloadDashboardCsvFile({ selectedRange, selectedTiers, selectedStatus, selectedCompanies });
    const companyStatsUI = useCompanyStatsUI();

    const { isLoading, activeCompanies, inactiveCompanies, pieData } = useActiveInactiveCompanies();
    const { isCreditLoading, totalCreditCount, creditedCompanies } = useCompaniesCredit();
    const { isCostLoading, totalCostCount, companiesCostData } = useCompaniesAiCost();
    const { badRecordsData } = useBadRecordsFailed();
    const { isFailedCasesLoading, totalFailedCases, failedCasesData } = useFailedCasesByCompany();
    const { isSubscriptionLoading, rollOverCreditAnalytics, alaCarteCreditAnalytics } = useCompanySubscriptionsCredit();
    const { companiesData } = useUpcomingCompany();

    // flatten failed cases so the modal config can consume it directly
    const failedCasesTableData = useMemo(
        () => failedCasesData?.flatMap(company =>
            (company.failedCases || []).map(caseId => ({
                database: company.database || company.companyName,
                caseId
            }))
        ) || [],
        [failedCasesData]
    );

    // pre-compute stat cards with resolved loading states
    const statCards = useMemo(
        () => renderSuperDashboardStatCards({
            activeCompanies,
            inactiveCompanies,
            totalCreditCount,
            totalFailedCases,
            isLoading,
            isCreditLoading,
            isFailedCasesLoading
        }),
        [activeCompanies, inactiveCompanies, totalCreditCount, totalFailedCases, isLoading, isCreditLoading, isFailedCasesLoading]
    );

    return {
        downloadDashboardCsvFile,
        ...companyStatsUI,
        isLoading,
        activeCompanies,
        inactiveCompanies,
        pieData,
        isCreditLoading,
        totalCreditCount,
        creditedCompanies,
        isCostLoading,
        totalCostCount,
        companiesCostData,
        badRecordsData,
        isFailedCasesLoading,
        totalFailedCases,
        failedCasesData,
        failedCasesTableData,
        isSubscriptionLoading,
        rollOverCreditAnalytics,
        alaCarteCreditAnalytics,
        companiesData,
        statCards
    };
};

export default useSuperDashboard;
